import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building, CheckCircle, Mail, X } from 'lucide-react';

const MailCompanyContactsModal = ({
  companyContacts,
  currentPage,
  deselectAllCompanyContacts,
  isEmailSelected,
  paginateItems,
  renderPagination,
  selectAllCompanyContacts,
  selectContactEmail,
  selectedCompany,
  setShowCompanyContacts
}) => {
  const { t } = useTranslation('admin');
  return (
    <>
        <div data-testid="mail-company-contacts-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1400] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h3 className="text-xl font-bold text-slate-900 flex items-center">
                  <Building size={24} className="mr-3 text-purple-600" />
                  {t('mail.contactsOf', { name: selectedCompany.name })}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {t('mail.contactsAndEmails', { contacts: companyContacts.length, emails: companyContacts.reduce((sum, c) => sum + c.emails.length, 0) })}
                </p>
              </div>
              <button
                onClick={() => setShowCompanyContacts(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-end space-x-2">
              <button
                onClick={selectAllCompanyContacts}
                className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center space-x-2"
              >
                <CheckCircle size={14} />
                <span>{t('mail.selectAll')}</span>
              </button>
              <button
                onClick={deselectAllCompanyContacts}
                className="px-4 py-1.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm font-medium"
              >
                {t('mail.deselectAll')}
              </button>
            </div>

            {/* Contacts List */}
            <div className="flex-1 overflow-y-auto p-4">
              {companyContacts.length === 0 ? (
                <div className="text-center py-12">
                  <Building size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">{t('mail.noContactForCompany')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {paginateItems(companyContacts, currentPage.companyContacts).map((contact) => (
                    <div key={contact.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 mb-2">{contact.contact_name}</p>
                          <div className="space-y-1">
                            {contact.emails.map((email, index) => (
                              <button
                                key={index}
                                onClick={() => {
                                  selectContactEmail(email);
                                }}
                                className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                                  isEmailSelected(email)
                                    ? 'bg-green-100 text-green-800 border border-green-300'
                                    : 'bg-white text-indigo-600 hover:bg-indigo-50 border border-slate-200'
                                }`}
                              >
                                <div className="flex items-center space-x-2">
                                  <Mail size={14} />
                                  <span className="text-sm">{email}</span>
                                </div>
                                {isEmailSelected(email) && (
                                  <CheckCircle size={16} className="text-green-600" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium">
                          {t('mail.badgeContact')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {renderPagination('companyContacts', companyContacts.length)}

            {/* Footer */}
            <div className="p-4 border-t border-slate-200">
              <button
                onClick={() => setShowCompanyContacts(false)}
                className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
              >
                {t('mail.back')}
              </button>
            </div>
          </div>
        </div>
    </>
  );
};

export default MailCompanyContactsModal;
